import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthenticationService, SSOService } from 'clan-auth';
import moment from 'moment';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

import { Locale } from '../interfaces';
import Address from '../models/Address';
import ClanMyData, { Insurances, PersonalInformation } from '../models/ClanMyData';
import Foundation from '../models/foundation/Foundation';
import Role from '../models/Role';
import SimulationCertificate from '../models/SimulationCertificate';
import SimulationPurchase from '../models/SimulationPurchase';
import { Assignee } from '../models/Transactions/Assignee';
import SimulationInsuranceCertificateTransaction from '../models/Transactions/SimulationInsuranceCertificateTransaction';
import WefSimStartResponse from '../models/WefSimStartResponse';
import WefSimStart from '../models/Transactions/WefSimStart';
import WhoAmI from '../models/WhoAmI';
import { LocaleService } from './locale.service';
import { SkribbleLink } from '../models/SkribbleLink';
import PensionFund from '../models/PensionFund';
import Person from '../models/Person';
import Company from '../models/Company';
import TransactionResponce, { TransactionType } from '../models/CreatePersonResponce';

import WefSimExecute from '../models/Transactions/WefSimExecute';
import WefSimEnd from '../models/Transactions/WefSimEnd';
import WefSimEndResponse from '../models/WefSimEndResponse';
import SavingPlan from '../models/Transactions/SavingPlan';
import SavingPlanResponse from '../models/SavingPlanResponse';
import Termination from '../models/Transactions/Termination';
import retirementExecute from '../models/Transactions/retirementExecute';
import { RoleType } from '../models/RoleType';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    // Todo: all requests should use the same url endpoint
    private twoFA = environment.authApiUrl;
    private clanMyData: ReplaySubject<ClanMyData> = new ReplaySubject<ClanMyData>(1);
    private pensionFund: ReplaySubject<PensionFund> = new ReplaySubject<PensionFund>();
    private foundation: ReplaySubject<Foundation> = new ReplaySubject<Foundation>(1);
    private currentPerson: ReplaySubject<Person> = new ReplaySubject<Person>(1);
    private company: ReplaySubject<Company> = new ReplaySubject<Company>(1);

    private selectedCompaniesData: Role[];
    private personsListData: Person[];
    private companyPersonsList: ReplaySubject<Person[]> = new ReplaySubject<[]>();
    private selectedCompanies: ReplaySubject<Role[]> = new ReplaySubject<Role[]>(1);
    private allCompanies: ReplaySubject<Role[]> = new ReplaySubject<Role[]>();
    private companyNameMap: ReplaySubject<Map<string, string>> = new ReplaySubject<Map<string, string>>();
    private isNotPensioner: ReplaySubject<boolean> = new ReplaySubject<boolean>(null);
    private isEmployer: ReplaySubject<boolean> = new ReplaySubject<boolean>(null);
    private isEmployee: ReplaySubject<boolean> = new ReplaySubject<boolean>(null);
    private hasAK: ReplaySubject<boolean> = new ReplaySubject<boolean>(null);

    constructor(
        private http: HttpClient,
        private localeService: LocaleService,
        private authenticationService: AuthenticationService
    ) {}

    get $allCompanies(): Observable<Role[]> {
        return this.allCompanies.asObservable();
    }

    get $companyNameMap(): Observable<Map<string, string>> {
        return this.companyNameMap.asObservable();
    }

    get $clanMyData(): Observable<ClanMyData> {
        return this.clanMyData.asObservable();
    }

    get $pensionFund(): Observable<PensionFund> {
        return this.pensionFund.asObservable();
    }

    get $foundation(): Observable<Foundation> {
        return this.foundation.asObservable();
    }

    get $currentPerson(): Observable<Person> {
        return this.currentPerson.asObservable();
    }

    get $company(): Observable<Company> {
        return this.company.asObservable();
    }

    get $pensionFundName(): Observable<string> {
        return this.$pensionFund.pipe(
            map((fund) => {
                return fund.name;
            })
        );
    }

    get $companyObject(): Observable<Company> {
        return this.$company.pipe(
            map((data) => {
                return data;
            })
        );
    }
    get apiUrl(): string {
        return sessionStorage.getItem('api-url');
    }

    get $userData(): Observable<PersonalInformation> {
        return this.$clanMyData.pipe(
            map((clanMyData) => {
                clanMyData.personalInformation.yearOfBirth = moment(clanMyData.personalInformation.dateOfBirth).year();
                clanMyData.personalInformation.age =
                    moment().year() - moment(clanMyData.personalInformation.dateOfBirth).year();
                return clanMyData.personalInformation;
            })
        );
    }

    get $currentUserData(): Observable<Person> {
        return this.$currentPerson.pipe(
            map((data) => {
                return data;
            })
        );
    }

    get $insurances(): Observable<Insurances[]> {
        return this.$clanMyData.pipe(map((clanMyData) => clanMyData.insurances));
    }

    get isNotPensioner$(): Observable<boolean> {
        return this.isNotPensioner.asObservable();
    }

    get isEmployer$(): Observable<boolean> {
        return this.isEmployer.asObservable();
    }

    get isEmployee$(): Observable<boolean> {
        return this.isEmployee.asObservable();
    }

    get hasAK$(): Observable<boolean> {
        return this.hasAK.asObservable();
    }

    setHasAK(value) {
        this.hasAK.next(value);
    }

    private getMyData() {
        return this.http.get<ClanMyData>(`${this.apiUrl}/mydata`);
    }

    public getCurrentPerson() {
        return this.http.get<Person>(`${this.apiUrl}/mydata`);
    }

    private getPensionFund(pensionFundId) {
        return this.http.get<PensionFund>(`${this.apiUrl}/pension-funds/${pensionFundId}`);
    }

    private getFoundation(foundationId: string) {
        return this.http.get<Foundation>(`${this.apiUrl}/foundations/${foundationId}`);
    }

    getCompany(companyId) {
        return this.http.get<Company>(`${this.apiUrl}/companies/${companyId}`);
    }
    private getAllCompanies() {
        return this.http.get<Company[]>(`${this.apiUrl}/companies`);
    }
    getSimulationCertificate(transaction: SimulationInsuranceCertificateTransaction) {
        return this.http.post<SimulationCertificate>(`${this.apiUrl}/simulations/certificate`, transaction);
    }

    getSimulationRetirement(transaction: SavingPlan) {
        return this.http.post<WefSimExecute>(`${this.apiUrl}/simulations/retirement`, transaction);
    }

    getSimulationRetirementExecute(transaction: retirementExecute) {
        return this.http.post<WefSimExecute>(`${this.apiUrl}/simulations/retirement-execute`, transaction);
    }

    getSavingPlan(transaction: SavingPlan) {
        return this.http.post<SavingPlanResponse>(`${this.apiUrl}/simulations/plan`, transaction);
    }

    getTermination(transaction: Termination) {
        return this.http.post<SavingPlanResponse>(`${this.apiUrl}/simulations/termination`, transaction);
    }

    getWef(transaction: WefSimStart) {
        return this.http.post<WefSimStartResponse>(`${this.apiUrl}/simulations/wefSimStart`, transaction);
    }

    getPreSimulation(transaction: WefSimStart) {
        return this.http.post<WefSimStartResponse>(`${this.apiUrl}/simulations/preSimStart`, transaction);
    }

    getWefExecute(transaction: WefSimExecute) {
        return this.http.post<WefSimEndResponse>(`${this.apiUrl}/simulations/wefSimExecute`, transaction);
    }

    private createEmployeeTransaction(body): Observable<TransactionResponce> {
        return this.http.post<TransactionResponce>(`${this.apiUrl}/transactions`, body);
    }

    getSimulationPurchase(body): Observable<SimulationPurchase> {
        return this.http.post<SimulationPurchase>(`${this.apiUrl}/simulations/purchase`, body);
    }

    checkForPendingEmailChange(email: string) {
        return this.http.get<boolean>(`${this.twoFA}/epwc/pendingEmailChange?email=${email}`);
    }

    // TODO so far unused remove?
    // openSimulationCertificate(): void {
    //     this.getSimulationCertificate().subscribe((res) => {
    //         const link = document.createElement('a');
    //         link.href = `data:${res.Dokument[0].MimeType};base64,${res.Dokument[0].Content}`;
    //         link.download = res.Dokument[0].Name;
    //         link.click();
    //         link.remove();
    //     });
    // }

    // TODO: extract message,title and signatures into parameters
    createSkribbleSignatureRequest(email: string, pdf: string): Observable<SkribbleLink> {
        return this.http.post<SkribbleLink>(`${this.apiUrl}/skribble/signature-requests`, {
            content: pdf,
            message: 'Sign document',
            signatures: [
                {
                    signer_email_address: 'florian@humanaized.com', // email,
                },
            ],
            title: 'Signing document from swarm pension',
        });
    }

    getData(): void {
        this.getMyData().subscribe((myData) => {
            this.clanMyData.next(myData);
            const determineStringValueInLocaleType = (value: string): value is Locale => !!(value as Locale);
            if (
                myData.personalInformation.language &&
                determineStringValueInLocaleType(myData.personalInformation.language)
            ) {
                // TODO: remove comment
                // this.localeService.changeLocale(myData.personalInformation.language);
            }
            //TODO: multiple insurances
            if (myData?.insurances?.length > 0 && myData.insurances[0]?.pensionFundId) {
                this.getPensionFund(myData.insurances[0]?.pensionFundId).subscribe((fund) => {
                    this.pensionFund.next(fund);
                });
            }
            if (myData?.insurances?.length > 0 && myData?.insurances[0]?.foundationId) {
                this.getFoundation(myData.insurances[0]?.foundationId).subscribe({
                    next: (foundation) => {
                        this.foundation.next(foundation);
                    },
                    error: (error) => {
                        //TODO: what to do in error case?
                        console.error(error);
                    },
                });
            }
            this.getAllCompanies().subscribe({
                next: (companies) => {
                    let map: Map<string, string> = new Map<string, string>();
                    for (const company of companies) {
                        map.set(company.companyId, company.name);
                    }
                    this.companyNameMap.next(map);
                },
            });
        });

        this.getCurrentPerson().subscribe(
            (data: Person) => {
                this.currentPerson.next(data);

                const parsedToken = SSOService.parseJwt(this.authenticationService.authToken);
                const hasPensionerRole = !!parsedToken.realm_access.roles.find((x) =>
                    x.includes(RoleType.PENSIONER.toLowerCase())
                );
                const hasEmployerRole = !!parsedToken.realm_access.roles.find((x) => {
                    if (x === RoleType.EMPLOYER.toLowerCase()) {
                        return true;
                    }
                });

                const hasEmployeeRole = !!parsedToken.realm_access.roles.find((x) => {
                    if (x === RoleType.EMPLOYEE.toLowerCase()) {
                        return true;
                    }
                });

                this.isNotPensioner.next(hasPensionerRole ? false : true);
                this.isEmployer.next(hasEmployerRole ? true : false);
                this.isEmployee.next(hasEmployeeRole ? true : false);

                if (data?.insurances?.length > 0 && data.insurances[0].companyId) {
                    this.getCompany(data.insurances[0].companyId).subscribe((c) => {
                        this.company.next(c);
                    });
                } else {
                    const firstRole = data.roles.find((role) => role.companyId);
                    const { companyId, pensionFundId, foundationId } = firstRole;
                    this.getCompany(companyId).subscribe((c) => {
                        this.company.next(c);
                    });
                    this.getPensionFund(pensionFundId).subscribe((p) => {
                        this.pensionFund.next(p);
                    });
                    this.getFoundation(foundationId).subscribe((f) => {
                        this.foundation.next(f);
                    });
                }
            },
            (error) => {
                console.log('Error while fetching current person', error);
            }
        );
    }

    // Give the person for originator information and the mutated address
    changeAddressTransaction(person: Person, address: Address): Observable<TransactionResponce> {
        const body = {
            company: person.insurances[0].foundationId,
            tenant: person.insurances[0].foundationId,
            originator: {
                personId: person.personId,
                companyId: person.insurances[0].companyId,
                pensionFundId: person.insurances[0].pensionFundId,
                foundationId: person.insurances[0].foundationId,
            },
            transactionType: TransactionType.EMPLOYEE_MUTATION_ADDRESS,
            assignee: Assignee.PVCLAN,
            data: {
                addresses: [address],
            },
        };
        return this.createEmployeeTransaction(body);
    }

    endPurchaseTransaction(person: Person): Observable<TransactionResponce> {
        const body = {
            originator: {
                personId: person.personId,
                companyId: person.insurances[0].companyId,
                pensionFundId: person.insurances[0].pensionFundId,
                foundationId: person.insurances[0].foundationId,
            },
            transactionType: TransactionType.EMPLOYEE_PURCHASE,
            assignee: Assignee.PVCLAN,
            data: {
                personId: person.personId,
                insurances: [
                    {
                        calculationDate: person.insurances[0].calculationDate,
                        maxPurchase: person.insurances[0].maxPurchase,
                    },
                ],
            },
        };

        return this.createEmployeeTransaction(body);
    }
    public removeSelectedCompany(role: Role): void {
        const i = this.selectedCompaniesData.indexOf(role);
        this.selectedCompaniesData.splice(i, 1);
        this.selectedCompanies.next(Array.from(this.selectedCompaniesData));
    }

    public addSelectedCompany(role: Role): void {
        this.selectedCompaniesData.push(role);
        this.selectedCompanies.next(Array.from(this.selectedCompaniesData));
    }
}
